import { createContext, useContext, useState, ReactNode } from "react";
import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
} from "./catalyst/alert";
import { Button } from "./catalyst/button";

interface ConfirmDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  title: string;
  message: string;
  cancelBtnText: string;
  confirmBtnText: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  setIsOpen,
  title,
  message,
  cancelBtnText,
  confirmBtnText,
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const handleCancel = () => {
    onCancel();
    setIsOpen(false);
  };

  const handleConfirm = () => {
    onConfirm();
    setIsOpen(false);
  };

  return (
    <Alert open={isOpen} onClose={setIsOpen}>
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
      <AlertActions>
        <Button plain onClick={handleCancel}>
          {cancelBtnText}
        </Button>
        <Button onClick={handleConfirm}>{confirmBtnText}</Button>
      </AlertActions>
    </Alert>
  );
};

interface ContextInterface {
  openDialog: (props: Partial<ConfirmDialogProps>) => void;
  closeDialog: () => void;
}
const ConfirmationDialogContext = createContext({} as ContextInterface);

export const useConfirmationDialog = () => {
  return useContext(ConfirmationDialogContext);
};

export const ConfirmationDialogProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState({
    title: "",
    message: "",
    cancelBtnText: "Cancel",
    confirmBtnText: "Confirm",
    onConfirm: () => {},
    onCancel: () => {},
  });

  const openDialog = (props: Partial<ConfirmDialogProps>) => {
    setDialogProps({ ...dialogProps, ...props });
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  return (
    <ConfirmationDialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      <ConfirmDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        title={dialogProps.title}
        message={dialogProps.message}
        cancelBtnText={dialogProps.cancelBtnText}
        confirmBtnText={dialogProps.confirmBtnText}
        onConfirm={dialogProps.onConfirm}
        onCancel={dialogProps.onCancel}
      />
    </ConfirmationDialogContext.Provider>
  );
};
